/**
 * KnowledgeSidebar — vibe-designer 1.169.0 Phase 2.
 *
 * Left nav: Overview + 6 services. Per-service availability dot reads
 * from knowledgeStore.overview. Selected item highlighted via
 * aria-current=page (also visually via accent background).
 */
import clsx from 'clsx';
import { BookOpen } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useTenantStore } from '@/stores/tenantStore';
import { SERVICE_META, type KnowledgeServiceKey } from './serviceMeta';
import type { KnowledgeServiceStats } from '@/services/knowledgeApi';

export type KnowledgeNavKey =
  | 'overview'
  | 'mind'
  | 'cortex'
  | 'brain'
  | 'sight'
  | 'cognition'
  | 'obsidian'
  | 'web_search'
  | 'synapse';

interface KnowledgeSidebarProps {
  selectedNav: KnowledgeNavKey;
  onSelect: (key: KnowledgeNavKey) => void;
}

const SERVICE_ITEMS: Array<{
  key: Exclude<KnowledgeNavKey, 'overview'>;
  label: string;
}> = [
  { key: 'mind', label: 'THE MIND' },
  { key: 'cortex', label: 'THE CORTEX' },
  { key: 'brain', label: 'THE BRAIN' },
  { key: 'sight', label: 'THE SIGHT' },
  { key: 'cognition', label: 'THE COGNITION' },
  { key: 'obsidian', label: 'Obsidian Wiki' },
  // 1.185.0 Phase 7.2 — Web Search is NOT a corpus (no count/label/secondary).
  // Availability sourced from tenantStore.mcpCapabilities.web_search rather
  // than knowledgeStore.overview (which only carries the 6 corpora).
  { key: 'web_search', label: 'Web Search' },
  // 2026-07-02 — THE SYNAPSE is the knowledge LOOP (wiki compiler), also not
  // a corpus. Availability sourced from knowledgeStore.synapseStatus (its own
  // status route), not overview.
  { key: 'synapse', label: 'THE SYNAPSE' },
];

function findService(
  overview: KnowledgeServiceStats[] | null,
  key: KnowledgeServiceStats['service'],
): KnowledgeServiceStats | undefined {
  if (!overview) return undefined;
  return overview.find((s) => s.service === key);
}

export function KnowledgeSidebar({ selectedNav, onSelect }: KnowledgeSidebarProps) {
  const overview = useKnowledgeStore((s) => s.overview);
  // 1.185.0 Phase 7.2 — web_search availability is NOT in overview[].
  // Sourced from /api/me's mcp_capabilities map via tenantStore.
  const mcpCapabilities = useTenantStore((s) => s.mcpCapabilities);
  // 2026-07-02 — SYNAPSE availability from its own status route.
  const synapseStatus = useKnowledgeStore((s) => s.synapseStatus);

  return (
    <nav
      aria-label="Knowledge navigation"
      className="w-64 flex-shrink-0 border-r border-vibe-border bg-vibe-bg-secondary p-2"
    >
      <button
        type="button"
        data-testid="knowledge-nav-overview"
        aria-current={selectedNav === 'overview' ? 'page' : undefined}
        onClick={() => onSelect('overview')}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded mb-1 transition-colors',
          selectedNav === 'overview'
            ? 'bg-vibe-accent/10 text-vibe-text'
            : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text',
        )}
      >
        <BookOpen className="w-4 h-4" />
        Overview
      </button>
      <div className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary px-3 py-2 mt-2">
        Services
      </div>
      {SERVICE_ITEMS.map(({ key, label }) => {
        // Phase 7.2 — web_search availability comes from mcpCapabilities,
        // not from knowledgeStore.overview. The 6 corpora keep the existing
        // overview-derived lookup.
        let available: boolean;
        if (key === 'web_search') {
          available = mcpCapabilities?.web_search ?? false;
        } else if (key === 'synapse') {
          available = synapseStatus?.available ?? false;
        } else {
          const svc = findService(overview, key);
          available = svc?.available ?? false;
        }
        const isActive = selectedNav === key;
        const meta = SERVICE_META[key as KnowledgeServiceKey];
        const ServiceIcon = meta.Icon;
        return (
          <button
            key={key}
            type="button"
            data-testid={`knowledge-nav-${key}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onSelect(key)}
            title={meta.tooltip}
            className={clsx(
              'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded mb-1 transition-colors',
              isActive
                ? 'bg-vibe-accent/10 text-vibe-text'
                : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text',
            )}
          >
            <span className="flex items-center gap-2 truncate">
              <ServiceIcon
                data-testid={`knowledge-nav-${key}-icon`}
                className={clsx('w-4 h-4 flex-shrink-0', meta.textClass)}
              />
              <span className="truncate">{label}</span>
            </span>
            <span
              data-testid={`knowledge-nav-${key}-dot`}
              className={clsx(
                'inline-block w-2 h-2 rounded-full flex-shrink-0',
                available ? 'bg-vibe-success' : 'bg-vibe-text-secondary',
              )}
              aria-label={available ? 'available' : 'unavailable'}
            />
          </button>
        );
      })}
    </nav>
  );
}
